export const LOGO_MAX_BYTES = 512 * 1024;
export const LOGO_MIN_HEIGHT_PX = 64;
export const LOGO_MAX_ASPECT = 6;
export const LOGO_RASTER_PX = 512;
export const LOGO_ACCEPT = '.png,.svg,image/png,image/svg+xml';

export type LogoProblem = 'format' | 'shape';

export class LogoError extends Error {
  constructor(readonly problem: LogoProblem) {
    super(problem);
  }
}

const isPng = (file: File) => file.type === 'image/png' || /\.png$/i.test(file.name);
const isSvg = (file: File) => file.type === 'image/svg+xml' || /\.svg$/i.test(file.name);

interface Size {
  readonly width: number;
  readonly height: number;
}

export const checkLogoShape = ({ width, height }: Size, raster: boolean): void => {
  if (width <= 0 || height <= 0) throw new LogoError('shape');
  const aspect = width / height;
  if (aspect > LOGO_MAX_ASPECT || aspect < 1) throw new LogoError('shape');
  if (raster && height < LOGO_MIN_HEIGHT_PX) throw new LogoError('shape');
};

const loadImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new LogoError('format'));
    image.src = url;
  });

/** An SVG with no size of its own is drawn as a square. */
const svgSize = (image: HTMLImageElement): Size =>
  image.naturalWidth > 0 && image.naturalHeight > 0
    ? { width: image.naturalWidth, height: image.naturalHeight }
    : { width: LOGO_RASTER_PX, height: LOGO_RASTER_PX };

const rasterize = (image: HTMLImageElement, { width, height }: Size): Promise<Blob> => {
  const scale = LOGO_RASTER_PX / Math.max(width, height);
  const canvas = document.createElement('canvas');
  // Rounded up, so the drawn PNG is never stretched past the shape limits the server checks.
  canvas.width = Math.ceil(width * scale);
  canvas.height = Math.ceil(height * scale);
  canvas.getContext('2d')?.drawImage(image, 0, 0, canvas.width, canvas.height);
  return new Promise((resolve, reject) =>
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new LogoError('format'))), 'image/png'),
  );
};

/** An SVG is rasterised here so that no SVG, and no script inside one, is ever served. */
export const prepareLogo = async (file: File): Promise<Blob> => {
  const svg = isSvg(file);
  if ((!svg && !isPng(file)) || file.size > LOGO_MAX_BYTES) throw new LogoError('format');
  const url = URL.createObjectURL(file);
  try {
    const image = await loadImage(url);
    if (!svg) {
      checkLogoShape({ width: image.naturalWidth, height: image.naturalHeight }, true);
      return file;
    }
    const size = svgSize(image);
    checkLogoShape(size, false);
    const png = await rasterize(image, size);
    if (png.size > LOGO_MAX_BYTES) throw new LogoError('format');
    return png;
  } finally {
    URL.revokeObjectURL(url);
  }
};
